/**
 * ModalGoToEdgenAiContent - 跨端公共内容组件
 *
 * 进入Edgen AI的提示弹窗中，可滚动内容部分，Web 和 App 共用
 */

import React from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from "../platformComponents";
import { i18nCrossUtil, tranformPx } from "@adgm/libs";
import { colors, fontFamily, fontWeight } from "../styles/common";

/**
 * ModalGoToEdgenAiContent Props
 */
interface ModalGoToEdgenAiContentProps {
  /**
   * 滚动区域高度
   */
  scrollViewHeight?: number;
  /**
   * URL点击事件处理函数
   */
  onUrlPress?: () => void;
}

export const ModalGoToEdgenAiContent: React.FC<ModalGoToEdgenAiContentProps> = ({
  scrollViewHeight = 335,
  onUrlPress,
}) => {
  const crossIntl = i18nCrossUtil.t();
  const edgenUrl = crossIntl["third.party.service.warning.intro.url"];

  // 五个要点的 i18n key 数组
  const sectionKeys = [
    "third.party.service.warning.section.third.party.service",
    "third.party.service.warning.section.not.professional.advice",
    "third.party.service.warning.section.accuracy.and.reliability",
    "third.party.service.warning.section.limitation.of.liability",
    "third.party.service.warning.section.fees.and.charges",
  ] as const;

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Text style={styles.iconText}>!</Text>
      </View>

      <Text style={styles.title}>{crossIntl["third.party.service.warning.title"]}</Text>

      {/* 可滚动内容区域 */}
      <ScrollView
        style={[styles.scrollView, { height: scrollViewHeight }]}
        {...({ showsVerticalScrollIndicator: true, nestedScrollEnabled: true } as any)}>
        <TouchableOpacity activeOpacity={1} style={styles.scrollContent}>
          {/* 介绍段落 */}
          <Text style={styles.paragraphIntro}>
            {crossIntl["third.party.service.warning.intro.prefix"]}
            <Text style={styles.paragraphIntroBold}>
              {crossIntl["third.party.service.warning.intro.edgenAi"]}
            </Text>
            {crossIntl["third.party.service.warning.intro.suffix"]}
            <Text style={styles.paragraphIntroLink} onPress={onUrlPress}>
              {edgenUrl}
            </Text>
            {crossIntl["third.party.service.warning.intro.suffix2"]}
          </Text>

          {/* 五个要点 */}
          {sectionKeys.map(key => (
            <View key={key} style={styles.section}>
              <Text style={styles.sectionTitle}>{crossIntl[`${key}.title`]}</Text>
              <Text style={styles.sectionContent}>{crossIntl[`${key}.content`]}</Text>
            </View>
          ))}
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    width: "100%",
  },
  iconContainer: {
    width: tranformPx(48),
    height: tranformPx(48),
    borderRadius: tranformPx(24),
    backgroundColor: colors.highlightColor,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: tranformPx(24),
  },
  iconText: {
    fontSize: tranformPx(24),
    color: colors.white,
    fontFamily: fontFamily.commonBold,
    fontWeight: fontWeight.f700,
  },
  title: {
    fontSize: tranformPx(20),
    lineHeight: tranformPx(28),
    fontFamily: fontFamily.commonMedium,
    fontWeight: fontWeight.f500,
    color: colors.white,
    marginBottom: tranformPx(24),
    textAlign: "center",
  },
  scrollView: {
    width: "100%",
  },
  scrollContent: {
    width: "100%",
  },
  paragraphIntro: {
    fontSize: tranformPx(14),
    lineHeight: tranformPx(22),
    fontFamily: fontFamily.commonRegular,
    fontWeight: fontWeight.f400,
    color: colors.white18,
    textAlign: "left",
    marginBottom: tranformPx(16),
  },
  paragraphIntroBold: {
    fontSize: tranformPx(14),
    lineHeight: tranformPx(22),
    fontFamily: fontFamily.commonBold,
    fontWeight: fontWeight.f700,
    color: colors.white,
  },
  paragraphIntroLink: {
    fontSize: tranformPx(14),
    lineHeight: tranformPx(22),
    fontFamily: fontFamily.commonRegular,
    fontWeight: fontWeight.f400,
    color: colors.highlightColor,
    textDecorationLine: Platform.OS === "web" ? "underline" : "none",
  },
  section: {
    marginBottom: tranformPx(16),
  },
  sectionTitle: {
    fontSize: tranformPx(14),
    lineHeight: tranformPx(22),
    fontFamily: fontFamily.commonBold,
    fontWeight: fontWeight.f700,
    color: colors.white,
    textAlign: "left",
    marginBottom: tranformPx(8),
  },
  sectionContent: {
    fontSize: tranformPx(14),
    lineHeight: tranformPx(22),
    fontFamily: fontFamily.commonRegular,
    fontWeight: fontWeight.f400,
    color: colors.white18,
    textAlign: "left",
  },
});
